import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, filter, of, switchMap } from 'rxjs';
import { CatalogService } from '../../services/catalog.service';
import { CartService } from '../../services/cart.service';
import { WishlistService } from '../../services/wishlist.service';
import { Product, salePrice } from '../../models/product.model';
import { Category } from '../../models/catalog.model';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, FormsModule],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent {
  private catalog = inject(CatalogService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly cart = inject(CartService);
  readonly wishlist = inject(WishlistService);

  readonly categories = toSignal(this.catalog.categories$, { initialValue: [] as Category[] });

  readonly scrolled = signal(false);
  readonly onHero = signal(true);
  readonly menuOpen = signal(false);
  readonly shopOpen = signal(false);
  readonly searchOpen = signal(false);

  readonly searchTerm = signal('');
  readonly searchResults = signal<Product[]>([]);
  readonly searching = signal(false);
  private readonly search$ = new Subject<string>();

  readonly salePrice = salePrice;

  constructor() {
    // Transparent navbar only on the landing hero.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((e) => {
        this.onHero.set(e.urlAfterRedirects.split('?')[0].split('#')[0] === '/');
        this.closeAll();
      });

    this.search$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const q = term.trim();
          if (q.length < 2) {
            this.searching.set(false);
            return of<Product[]>([]);
          }
          this.searching.set(true);
          return this.catalog.getProducts({ q });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (products) => {
          this.searchResults.set(products.slice(0, 6));
          this.searching.set(false);
        },
        error: () => this.searching.set(false),
      });
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.scrolled.set(window.scrollY > 24);
  }

  get transparent(): boolean {
    return this.onHero() && !this.scrolled() && !this.menuOpen() && !this.searchOpen();
  }

  onSearchInput(value: string): void {
    this.searchTerm.set(value);
    this.search$.next(value);
  }

  submitSearch(): void {
    const q = this.searchTerm().trim();
    if (!q) return;
    this.closeAll();
    this.router.navigate(['/shop'], { queryParams: { q } });
  }

  goToProduct(product: Product): void {
    this.closeAll();
    this.router.navigate(['/product', product.slug || product.id]);
  }

  toggleMenu(): void {
    this.menuOpen.update((v) => !v);
    if (this.menuOpen()) this.searchOpen.set(false);
  }

  toggleSearch(): void {
    this.searchOpen.update((v) => !v);
    if (this.searchOpen()) this.menuOpen.set(false);
  }

  closeAll(): void {
    this.menuOpen.set(false);
    this.shopOpen.set(false);
    this.searchOpen.set(false);
    this.searchTerm.set('');
    this.searchResults.set([]);
  }
}
